/**
 * @file        src/pages/package/SimpleLookupPage.jsx
 * @description Config-driven CRUD screen for simple lookup entities (Payment
 *              Mode, Contact Detail, Meal, Inclusion, Exclusion). Renders a
 *              paginated table plus a modal add/edit form. Drive it with a
 *              `fields` config; it auto-adds Status + Updated On + Action.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Package Management: lookup UI factory).
 *   - 2026-07-13 Claude — `date` fields now render the themed DatePicker instead
 *     of a native <input type="date"> (OS calendar, un-themable).
 *   - 2026-08-03 Claude — New `brand` field type: BrandSelect in the form (registry
 *     codes, no default — creator must pick) and the brand's registry name in the
 *     table cell.
 *   - 2026-08-10 Claude — `date` fields accept `min` (a 'YYYY-MM-DD' string or a
 *     `(form) => string` — e.g. an expiry field's min tracks today AND its paired
 *     start-date field) so the calendar itself can't offer an invalid date.
 *     Console-wide date rule pass — pairs with each page's own `validate`.
 *   - 2026-08-10 Claude — New optional `statusOf(row)` prop: when supplied it
 *     replaces the generic `is_active`-only Status chip with the caller's own
 *     `{ label, tone }` (e.g. Coupon List's live Active/Expired/Exhausted
 *     status). Omit it and every existing consumer is unchanged.
 *   - 2026-09-12 Claude — Fields accept `form: false` — the mirror of the existing
 *     `table: false`. The field keeps its table column and is still submitted
 *     (from its `default` on create, or the row's own value on edit) but gets no
 *     input in the modal. Used by the Convenience Fee screens, where the page
 *     ITSELF fixes "Applies To": a fee added on the Corporate screen is a
 *     corporate fee, and offering the choice only invited the wrong pick.
 *   - 2026-09-12 Claude — New optional `notice` prop: a one-line informational
 *     bar above the table. Added for the markup screens, which must state which
 *     rule wins when several match one fare — an admin previously had no way to
 *     tell from this screen. Omit it and existing consumers are unchanged.
 *   - 2026-09-15 Claude — `number` fields accept `max` (a number or a
 *     `(form) => number`, like `date`'s `min` already did), so a field whose
 *     ceiling depends on a sibling field — a percentage discount capped at 100
 *     vs an uncapped flat amount — can express it. Attribute only, not clamped
 *     on change; the page's validate() is the real gate.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-05 Claude — `select` fields accept `allowOther`: an "Other…" choice
 *                          that reveals a free-text input, for open-ended supplier
 *                          vocabularies (flight fare families) where a fixed list
 *                          can never be complete. A stored value that is not in
 *                          the list opens in that mode instead of reading blank,
 *                          and options match case/space/underscore-insensitively.
 *   - 2026-10-05 Claude — `nullable` fields send an explicit null when left blank.
 *                          A blank field is otherwise OMITTED from the body, so on
 *                          edit "—" kept the stored value: a Fare Type could be
 *                          set but never cleared back to "every fare type".
 *   - 2026-10-06 Claude — `select` fields accept `blankLabel`: what an empty value
 *                          MEANS ("All (Domestic + International)"), used for the
 *                          select's empty choice and for the table cell, instead
 *                          of a bare "—" that reads as "not set".
 *   - 2026-10-07 Claude — Fields accept `fromRow(row)`, for a value derived from
 *                          other columns rather than stored (B2B Flight Markup's
 *                          Markup Type = "is an airline code set"): it seeds the
 *                          edit form and renders the table cell. The page prop
 *                          `shapeBody(body)` maps such values back onto real
 *                          columns before the save.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import { useBrands } from '../../api/brands';
import { get, post, patch, del } from '../../api/client';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

// Icons available in the app's built-in set (src/components/Icon.jsx).
export const ICON_CHOICES = [
  'bed', 'plane', 'box', 'badge', 'card', 'wallet', 'bell', 'clock',
  'users', 'gear', 'grid', 'page', 'trend', 'download', 'filter', 'search',
  'check', 'plus', 'menu', 'refund',
];

function IconField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="icon-field">
      <div className="icon-field-row">
        <span className="icon-preview">
          {value ? <Icon name={value} size={20} /> : <span className="muted">none</span>}
        </span>
        <input readOnly value={value || ''} placeholder="Pick an icon…"
               onClick={() => setOpen((o) => !o)} style={{ cursor: 'pointer' }} />
        {value ? <button type="button" className="btn ghost" onClick={() => onChange('')}>Clear</button> : null}
        <button type="button" className="btn ghost" onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : 'Icon list'}
        </button>
      </div>
      {open ? (
        <div className="icon-grid">
          {ICON_CHOICES.map((n) => (
            <button type="button" key={n} title={n}
                    className={`icon-cell${value === n ? ' active' : ''}`}
                    onClick={() => { onChange(n); setOpen(false); }}>
              <Icon name={n} size={20} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Table cell for a `brand` field — the registry name for the 3-char code.
function BrandCell({ code }) {
  const { labelOf } = useBrands();
  return <>{labelOf(code)}</>;
}

function blankForm(fields) {
  const f = { is_active: true, language: 'en' };
  // `default` may be a function (e.g. auto-generate a fresh coupon code on each
  // "Add") — call it; otherwise use the static default.
  for (const fld of fields) f[fld.key] = typeof fld.default === 'function' ? fld.default() : (fld.default ?? '');
  return f;
}

/**
 * @param {object}   props
 * @param {string}   props.title
 * @param {string}   props.subtitle
 * @param {string}   props.endpoint     API path, e.g. '/payment-modes'
 * @param {string}   props.queryRoot    cache key root, e.g. 'payment-modes'
 * @param {string}   props.addLabel
 * @param {string}   props.entityLabel  singular, used in toasts/confirm
 * @param {Array}    props.fields       [{ key,label,type,options?,required?,table?,form?,full? }]
 *                                      `table:false` hides the column, `form:false`
 *                                      hides the input (the value still saves).
 * @param {boolean}  [props.embedded]   skip the PageHead (for tabbed hosts)
 * @param {object}   [props.listParams] extra query params for the LIST request —
 *                                      e.g. { applicable_for: 'flight' } so a
 *                                      product-scoped page shows only its own rows.
 * @param {object}   [props.fixedValues] values merged into every create/update body
 *                                      and NOT editable in the form — e.g. stamping
 *                                      the product a coupon belongs to.
 * @param {function}  [props.statusOf]  (row) => { label, tone } — overrides the
 *                                      default is_active-only Status chip, e.g. to
 *                                      fold in an expiry/usage window.
 * @param {function} [props.shapeBody] (body) => body — last step before a save,
 *                                      e.g. turning a derived (`fromRow`) field
 *                                      back into the columns it stands for.
 * @param {node}     [props.notice]   one-line informational bar rendered above the
 *                                      table (e.g. how overlapping rules resolve).
 */
export default function SimpleLookupPage({
  title, subtitle, endpoint, queryRoot, addLabel, entityLabel, fields, embedded = false,
  listParams, fixedValues, validate, statusOf, notice, shapeBody,
}) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [modal, setModal] = useState(null); // null | { mode:'create'|'edit', row? }
  const [form, setForm] = useState(() => blankForm(fields));
  const [errors, setErrors] = useState({});

  const qc = useQueryClient();
  const params = { page, pageSize, ...(listParams || {}) };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: [queryRoot, 'list', params],
    queryFn: () => get(endpoint, { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const saveMut = useMutation({
    // `fixedValues` are stamped server-bound on every write and are not editable in
    // the form — a coupon created on the Flight page is a flight coupon, and no
    // amount of form-fiddling can turn it into a hotel one.
    mutationFn: ({ id, body }) => {
      const payload = { ...body, ...(fixedValues || {}) };
      return id ? patch(`${endpoint}/${id}`, payload) : post(endpoint, payload);
    },
    onSuccess: (_r, vars) => {
      toast.ok(vars.id ? `${entityLabel} updated` : `${entityLabel} added`, 'Saved.');
      qc.invalidateQueries({ queryKey: [queryRoot] });
      setModal(null);
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`${endpoint}/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', `${entityLabel} removed.`);
      qc.invalidateQueries({ queryKey: [queryRoot] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  function openCreate() {
    setErrors({});
    setForm(blankForm(fields));
    setModal({ mode: 'create' });
  }

  function openEdit(row) {
    setErrors({});
    const f = { is_active: !!row.is_active, language: row.language || 'en' };
    for (const fld of fields) {
      const fallback = typeof fld.default === 'function' ? '' : (fld.default ?? '');
      // Date fields come back as datetimes — trim to YYYY-MM-DD for <input type=date>.
      let v = (fld.fromRow ? fld.fromRow(row) : row[fld.key]) ?? fallback;
      if (fld.type === 'date' && typeof v === 'string') v = v.slice(0, 10);
      f[fld.key] = v;
    }
    setForm(f);
    setModal({ mode: 'edit', row });
  }

  function onSubmit(e) {
    e.preventDefault();
    const errs = {};
    for (const fld of fields) {
      if (fld.required && String(form[fld.key] ?? '').trim() === '') errs[fld.key] = 'Required';
    }
    // Cross-field / custom rules (e.g. min ≤ max) the page supplies.
    if (validate) Object.assign(errs, validate(form) || {});
    if (Object.keys(errs).length) return setErrors(errs);
    setErrors({});

    const body = { is_active: !!form.is_active };
    for (const fld of fields) {
      const v = form[fld.key];
      if (v === '' || v == null) {
        // Omitted = "leave as stored" on a PATCH; a nullable field must be able
        // to go back to blank, so it says so explicitly.
        if (fld.nullable) body[fld.key] = null;
        continue;
      }
      body[fld.key] = fld.type === 'number' ? Number(v) : v;
    }
    saveMut.mutate({ id: modal?.row?.id, body: shapeBody ? shapeBody(body) : body });
  }

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(search);
  }

  async function onDelete(row) {
    const label = row.name || row.service || row.meal_type || `#${row.id}`;
    if (!(await confirm(`Delete ${entityLabel.toLowerCase()} “${label}”?`))) return;
    removeMut.mutate(row.id);
  }

  const tableFields = fields.filter((f) => f.table !== false);
  // Fields the modal renders. A `form:false` field is deliberately NOT dropped from
  // `fields`: blankForm/openEdit still seed it and onSubmit still sends it, so the
  // page's own `default` decides it on create and an existing row keeps its value
  // on edit (an edit from one screen must never silently re-scope a shared row).
  const formFields = fields.filter((f) => f.form !== false);

  const AddBtn = <button className="btn primary" onClick={openCreate}><Icon name="plus" /> {addLabel}</button>;

  return (
    <>
      {embedded
        ? <div className="row-act" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>{AddBtn}</div>
        : <PageHead title={title} subtitle={subtitle} actions={AddBtn} />}

      {notice ? <div className="notice-banner info">{notice}</div> : null}

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Search</label>
            <input placeholder="Type to search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  {tableFields.map((f) => <th key={f.key}>{f.label}</th>)}
                  <th>Status</th><th>Updated On</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={tableFields.length + 4} className="empty">Nothing here yet. Add your first one.</td></tr>
                ) : rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    {tableFields.map((f) => <td key={f.key}>{renderCell(f, row)}</td>)}
                    <td>{statusOf
                      ? <Chip tone={statusOf(row).tone}>{statusOf(row).label}</Chip>
                      : <Chip tone={row.is_active ? 'ok' : 'rej'}>{row.is_active ? 'Active' : 'Inactive'}</Chip>}</td>
                    <td>{fmtDate(row.updated_at)}</td>
                    <td className="row-act">
                      <button className="ib edit" aria-label="Edit" onClick={() => openEdit(row)}><Icon name="edit" /></button>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(row)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {modal ? (
        <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="lk-modal panel glass">
            <div className="lk-modal-head">
              <h3>{modal.mode === 'edit' ? `Edit ${entityLabel}` : addLabel}</h3>
              <button className="ib" aria-label="Close" onClick={() => setModal(null)}><Icon name="x" /></button>
            </div>
            <form className="form-grid" onSubmit={onSubmit}>
              {formFields.map((f) => (
                <div className={`field${f.full ? ' full' : ''}`} key={f.key}>
                  <label>{f.label} {f.required ? <span className="req">*</span> : null}</label>
                  {f.type === 'icon'
                    ? <IconField value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
                    : renderInput(f, form, setForm)}
                  {errors[f.key] ? <div className="field-error">{errors[f.key]}</div> : null}
                </div>
              ))}
              <div className="field">
                <label>Status</label>
                <label className="check">
                  <input type="checkbox" checked={!!form.is_active}
                         onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn ghost" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn primary" type="submit" disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

function renderCell(f, row) {
  const v = f.fromRow ? f.fromRow(row) : row[f.key];
  if (v == null || v === '') return <span className="muted">{f.blankLabel || '—'}</span>;
  if (f.type === 'brand') return <BrandCell code={v} />;
  if (f.type === 'date') return String(v).slice(0, 10);
  if (f.type === 'icon') {
    return ICON_CHOICES.includes(v)
      ? <span className="icon-cell sm"><Icon name={v} size={18} /></span>
      : String(v);
  }
  if (f.type === 'image' || f.key === 'icon') {
    return /^https?:\/\//.test(v)
      ? <img src={v} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />
      : String(v);
  }
  if (f.type === 'select') {
    const opt = findOption(f, v);
    return opt ? opt.label : String(v);
  }
  return String(v);
}

// `allowOther` fields hold supplier codes, whose spelling varies ("NDC_Xpress
// Value" vs "NDC_XPRESS_VALUE") — compare them the way the engine does.
const codeKey = (v) => String(v ?? '').trim().toUpperCase().replace(/[\s_-]+/g, '_');
function findOption(f, v) {
  const opts = f.options || [];
  return opts.find((o) => o.value === v)
    || (f.allowOther ? opts.find((o) => codeKey(o.value) === codeKey(v)) : undefined);
}

const OTHER = '__other__';

/** A select with an "Other…" entry that reveals a free-text input. */
export function SelectOrText({ f, value, onChange }) {
  const opt = findOption(f, value);
  const [other, setOther] = useState(value !== '' && !opt);
  return (
    <>
      <Select
        value={other ? OTHER : (opt ? opt.value : value)}
        onChange={(v) => {
          if (v === OTHER) { setOther(true); onChange(''); return; }
          setOther(false);
          onChange(v);
        }}
        placeholder={f.required ? 'Select' : '—'}
        options={[
          ...(f.required ? [] : [{ value: '', label: '—' }]),
          ...(f.options || []),
          { value: OTHER, label: f.otherLabel || 'Other (type it)…' },
        ]}
      />
      {other && (
        <input
          style={{ marginTop: 6 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.otherPlaceholder}
          maxLength={f.maxLength}
          autoFocus
        />
      )}
    </>
  );
}

function renderInput(f, form, setForm) {
  const val = form[f.key] ?? '';
  const set = (v) => setForm({ ...form, [f.key]: v });
  if (f.type === 'brand') {
    return <BrandSelect value={val} onChange={set} />;
  }
  if (f.type === 'select' && f.allowOther) {
    return <SelectOrText f={f} value={val} onChange={set} />;
  }
  if (f.type === 'select') {
    const blank = f.blankLabel || '—';
    return (
      <Select
        value={val}
        onChange={set}
        placeholder={f.required ? 'Select' : blank}
        options={[...(f.required ? [] : [{ value: '', label: blank }]), ...(f.options || [])]}
      />
    );
  }
  if (f.type === 'date') {
    const min = typeof f.min === 'function' ? f.min(form) : f.min;
    return <DatePicker value={val ? String(val).slice(0, 10) : ''} onChange={set} min={min} />;
  }
  if (f.type === 'textarea') {
    return <textarea rows={3} value={val} onChange={(e) => set(e.target.value)} placeholder={f.placeholder} />;
  }
  if (f.type === 'number') {
    const min = typeof f.min === 'function' ? f.min(form) : f.min;
    // `max` is an attribute only — unlike `min` it is NOT clamped on change,
    // because a caller's max can depend on another field (a percentage capped
    // at 100 vs an uncapped flat amount) and snapping mid-keystroke fights the
    // typist. The page's validate() is what actually rejects an over-max value.
    const max = typeof f.max === 'function' ? f.max(form) : f.max;
    return (
      <input
        type="number"
        min={min}
        max={max}
        step={f.step}
        value={val}
        onChange={(e) => {
          let v = e.target.value;
          if (v !== '' && min != null && Number(v) < min) v = String(min);
          set(v);
        }}
        placeholder={f.placeholder}
      />
    );
  }
  return <input value={val} onChange={(e) => set(e.target.value)} placeholder={f.placeholder} maxLength={f.maxLength} />;
}
